import makeActionCreator from 'src/lib/makeActionCreator';
import { State } from 'src/redux/reducers/RootReducer';
import {
  getEntitiesData,
  postDownloadEntitesData,
  postEmailEntitiesData,
  postOpenTabEntityData,
} from 'src/apis/QuerybuilderAPI';
import { ENTITIES, QuerybuilderResult } from 'src/models/QuerybuilderModel';
import { setFormStatus } from 'src/redux/actions/formActions';
import { FORM, FORM_STATUS } from 'src/constants/Form';
import { DownloadOptionsModel, FORMAT } from 'src/models/DownloadModel';
import { Filters, SEARCH_LOGICAL_OPERATORS } from 'src/models/AdvanceSearchModel';
import { SEARCH_OPERATORS, SEARCH_OPERATORS_PRIMITIVE } from 'src/constants/SearchOperators';
import { getFieldsFromColumns } from 'src/lib/FieldHelper';
import { kebabToSnake } from 'src/lib/QueryBuilderHelper';
import { DownloadOption } from 'src/lib/makeApiRequest';
import { setAlert } from 'src/redux/actions/alertActions';
import { ALERT_TYPE } from 'src/constants/AlertType';
import { normalizeInvoice } from 'src/redux/actions/invoiceActions';
import { normalizePayment } from 'src/redux/actions/paymentActions';
import { Route } from 'src/constants/routes';

export const SET_COLUMNS = 'SET_COLUMNS';
export const SET_ENTITY = 'SET_ENTITY';
export const SET_FIELDS = 'SET_FIELDS';
export const SET_FILTERS = 'SET_FILTERS';
export const RESET_FILTERS = 'RESET_FILTERS';
export const SET_OPERATOR = 'SET_OPERATOR';
export const SET_ORDERS = 'SET_ORDERS';
export const SET_PAGINATION = 'SET_PAGINATION';
export const SET_OPTIONS = 'SET_OPTIONS';
export const SET_QUERY_BUILDER_RESULT = 'SET_QUERY_BUILDER_RESULT';
export const SET_QUICK_SEARCH = 'SET_QUICK_SEARCH';
export const RESET_QUERY_BUILDER_RESULT = 'RESET_QUERY_BUILDER_RESULT';

export const setColumns = makeActionCreator(SET_COLUMNS);
export const setOptions = makeActionCreator(SET_OPTIONS);
export const resetQueryBuilderResult = makeActionCreator(RESET_QUERY_BUILDER_RESULT);
export const setEntity = makeActionCreator(SET_ENTITY);
export const setFields = makeActionCreator(SET_FIELDS);
export const setFilters = makeActionCreator(SET_FILTERS);
export const resetFilters = makeActionCreator(RESET_FILTERS);
export const setOperator = makeActionCreator(SET_OPERATOR);
export const setOrders = makeActionCreator(SET_ORDERS);
export const setPagination = makeActionCreator(SET_PAGINATION);
export const setQueryBuilderResult = makeActionCreator(SET_QUERY_BUILDER_RESULT);
export const setQuickSearch = makeActionCreator(SET_QUICK_SEARCH);

const shouldGetFields = (
  options: DownloadOptionsModel,
  querybuilderEntity: ENTITIES,
): boolean => (
  options.format === FORMAT.csv
  || (options.export_to_grid && options.format === FORMAT.pdf)
  || querybuilderEntity === ENTITIES.contacts
  || querybuilderEntity === ENTITIES.fundraisingTransactions
);

export const postDownloadEntitesAction: (
  entity: ENTITIES,
  options: DownloadOptionsModel,
) => ThunkedAction<State> = (
  entity: ENTITIES,
  options: DownloadOptionsModel,
) => async (dispatch: any, getState: any) => {
  const organisation = getState().currentOrganisation.id;
  const querybuilderEntity = entity === ENTITIES.taxReceipts ? ENTITIES.payments : entity;
  const { querybuilder } = getState();
  const {
    filters, order,
  } = querybuilder[querybuilderEntity];

  const getFieldFilter = () => {
    // Contacts or fundraisingTransactions → always use entity fields
    if (entity === ENTITIES.contacts || entity === ENTITIES.fundraisingTransactions) {
      return getFieldsFromColumns(getState(), entity);
    }

    // Export to grid with CSV/PDF → use querybuilder fields
    if (options.export_to_grid &&
      (options.format === FORMAT.csv || options.format === FORMAT.pdf)) {
      return getFieldsFromColumns(getState(), querybuilderEntity);
    }

    // Default case: non-grid exports → use entity fields
    if (!options.export_to_grid) {
      return getFieldsFromColumns(getState(), entity);
    }

    // Grid export + non-CSV/PDF + non-special entities → empty
    return [];
  };

  const query = {
    filters,
    order,
    options,
    fields: getFieldFilter(),
  };
  if (query.fields.length === 0) {
    Reflect.deleteProperty(query, 'fields');
  }
  if (options.all) {
    switch (entity) {
      case ENTITIES.fundraisingTransactions: {
        const pathSegments = window.location.pathname.split('/');
        const id = pathSegments[pathSegments.length - 1];
        query.filters = {
          and: [['event_transactions.event_id', '=', id]],
        };
        break;
      }
      default:
        Reflect.deleteProperty(query, 'filters');
    }
  }
  try {
    dispatch(setFormStatus({ [FORM.query_builder]: FORM_STATUS.processing }));
    dispatch(setAlert({
      type: ALERT_TYPE.download,
      code: 'messages.download_started',
    }));
    const response = await postDownloadEntitesData(organisation, entity, query, options);
    if (response.status === 400) {
      const errorCode = response.message?.split(' - ')[0];
      if (errorCode === '2008') {
        dispatch(setAlert({
          type: ALERT_TYPE.warning,
          code: 'messages.download_notfound',
        }));
        dispatch(setFormStatus({ [FORM.query_builder]: FORM_STATUS.error }));
        return;
      }
    }
    if (response.status !== 200) {
      dispatch(setAlert({
        type: ALERT_TYPE.error,
        code: 'Something went wrong',
      }));
      dispatch(setFormStatus({ [FORM.query_builder]: FORM_STATUS.error }));
      return;
    }
    dispatch(setAlert({
      type: ALERT_TYPE.success,
      code: 'messages.download_success',
    }));
    dispatch(setFormStatus({ [FORM.query_builder]: FORM_STATUS.success }));
  } catch (error) {
    dispatch(setAlert({
      type: ALERT_TYPE.error,
      code: 'messages.download_error',
    }));
    dispatch(setFormStatus({ [FORM.query_builder]: FORM_STATUS.error }));
  }
};

export const postDownloadSelectedEntitesAction:
(
  entity: ENTITIES,
  ids: number[],
  options: DownloadOptionsModel,
  customFilterField?: string,
  downloadOption?: DownloadOption,
) => ThunkedAction<State> =
  (
    entity: ENTITIES,
    ids: number[],
    options: DownloadOptionsModel,
    customFilterField?: string,
    downloadOption?: DownloadOption,
  ) => async (dispatch: any, getState: any) => {
    const organisation = getState().currentOrganisation.id;
    const querybuilderEntity = entity === ENTITIES.taxReceipts ? ENTITIES.payments : entity;
    const { querybuilder } = getState();
    const {
      order,
    } = querybuilder[querybuilderEntity] ?? [];

    const filters = {
      [SEARCH_LOGICAL_OPERATORS.and]: [
        [`${kebabToSnake(entity)}.${customFilterField ?? 'id'}`, SEARCH_OPERATORS.is_in, ids],
      ],
    } as Filters;

    const getFields = () => (
      shouldGetFields(options, querybuilderEntity)
        ? getFieldsFromColumns(getState(), querybuilderEntity)
        : []
    );

    const query = {
      filters,
      order,
      fields: getFields(),
      options,
    };
    if (query.fields.length === 0) {
      Reflect.deleteProperty(query, 'fields');
    }
    try {
      dispatch(setFormStatus({ [FORM.query_builder]: FORM_STATUS.processing }));
      dispatch(setAlert({
        type: ALERT_TYPE.download,
        code: 'messages.download_started',
      }));
      const response = await postDownloadEntitesData(
        organisation,
        entity,
        query,
        options,
        downloadOption,
      );
      if (response.status !== 200) {
        dispatch(setAlert({
          type: ALERT_TYPE.error,
          code: 'messages.download_error',
        }));
        dispatch(setFormStatus({ [FORM.query_builder]: FORM_STATUS.error }));
        return;
      }
      dispatch(setAlert({
        type: ALERT_TYPE.success,
        code: 'messages.download_success',
      }));
      dispatch(setFormStatus({ [FORM.query_builder]: FORM_STATUS.success }));
    } catch (error) {
      dispatch(setAlert({
        type: ALERT_TYPE.error,
        code: 'messages.download_error',
      }));
      dispatch(setFormStatus({ [FORM.query_builder]: FORM_STATUS.error }));
    }
  };

export const postEmailEntitesAction:
(entity: ENTITIES, options: DownloadOptionsModel) => ThunkedAction<State> =
  (entity: ENTITIES, options: DownloadOptionsModel) => async (dispatch: any, getState: any) => {
    const organisation = getState().currentOrganisation.id;
    const querybuilderEntity = entity === ENTITIES.taxReceipts ? ENTITIES.payments : entity;
    const { querybuilder } = getState();
    const {
      filters, order,
    } = querybuilder[querybuilderEntity];
    const getFields = () => (
      shouldGetFields(options, querybuilderEntity)
        ? getFieldsFromColumns(getState(), querybuilderEntity)
        : []
    );
    const query = {
      filters,
      order,
      fields: getFields(),
      options,
    };
    if (query.fields.length === 0) {
      Reflect.deleteProperty(query, 'fields');
    }
    try {
      const response = await postEmailEntitiesData(organisation, entity, query, options);
      if (response.status === 400) {
        const errorCode = response.message?.split(' - ')[0];
        if (errorCode === '2008') {
          dispatch(setAlert({
            type: ALERT_TYPE.warning,
            code: 'messages.download_notfound',
          }));
          dispatch(setFormStatus({ [FORM.query_builder]: FORM_STATUS.error }));
          return;
        }
      }
      if (response.status !== 200) {
        dispatch(setAlert({
          type: ALERT_TYPE.error,
          code: 'Something went wrong',
        }));
        dispatch(setFormStatus({ [FORM.query_builder]: FORM_STATUS.error }));
        return;
      }
      dispatch(setAlert({
        type: ALERT_TYPE.success,
        message: 'Request successfully sent',
      }));
      dispatch(setFormStatus({ [FORM.query_builder]: FORM_STATUS.success }));
    } catch (e) { /* Log the error here */
      dispatch(setAlert({
        type: ALERT_TYPE.error,
        message: 'Something went wrong',
      }));
      dispatch(setFormStatus({ [FORM.query_builder]: FORM_STATUS.error }));
    }
  };

export const postEmailSelectedEntitesAction:
(
  entity: ENTITIES,
  ids: number[],
  options: DownloadOptionsModel,
  customFilterField?: string
) => ThunkedAction<State> =
  (
    entity: ENTITIES,
    ids: number[],
    options: DownloadOptionsModel,
    customFilterField?: string,
  ) => async (dispatch: any, getState: any) => {
    const organisation = getState().currentOrganisation.id;
    const querybuilderEntity = entity === ENTITIES.taxReceipts ? ENTITIES.payments : entity;
    const { querybuilder } = getState();
    const {
      order,
    } = querybuilder[querybuilderEntity];
    const filters = {
      [SEARCH_LOGICAL_OPERATORS.and]: [
        [`${kebabToSnake(entity)}.${customFilterField ?? 'id'}`, SEARCH_OPERATORS.is_in, ids],
      ],
    } as Filters;
    const getFields = () => (
      shouldGetFields(options, querybuilderEntity)
        ? getFieldsFromColumns(getState(), querybuilderEntity)
        : []
    );
    const query = {
      filters,
      order,
      options,
      fields: getFields(),
    };
    if (query.fields.length === 0) {
      Reflect.deleteProperty(query, 'fields');
    }
    try {
      dispatch(setFormStatus({ [FORM.query_builder]: FORM_STATUS.processing }));
      const response = await postEmailEntitiesData(organisation, entity, query, options);
      if (response.status !== 200) {
        dispatch(setAlert({
          type: ALERT_TYPE.error,
          code: 'Something went wrong',
        }));
        dispatch(setFormStatus({ [FORM.query_builder]: FORM_STATUS.error }));
        return;
      }
      dispatch(setAlert({
        type: ALERT_TYPE.success,
        message: 'Request successfully sent',
      }));
      dispatch(setFormStatus({ [FORM.query_builder]: FORM_STATUS.success }));
    } catch (e) { /* Log the error here */
      dispatch(setAlert({
        type: ALERT_TYPE.error,
        message: 'Something went wrong',
      }));
      dispatch(setFormStatus({ [FORM.query_builder]: FORM_STATUS.error }));
    }
  };

export const postOpenSelectedTabEntityAction: (
  entity: ENTITIES,
  ids: number[],
  options: DownloadOptionsModel,
) => ThunkedAction<State> = (
  entity,
  ids,
  options,
) => async (dispatch, getState) => {
  const organisation = getState().currentOrganisation.id;
  const querybuilderEntity = entity === ENTITIES.taxReceipts ? ENTITIES.payments : entity;
  const { querybuilder } = getState();
  const { order } = querybuilder[querybuilderEntity] ?? [];

  const filters = {
    [SEARCH_LOGICAL_OPERATORS.and]: [
      [`${kebabToSnake(entity)}.payment_id`, SEARCH_OPERATORS.is_in, ids],
    ],
  } as Filters;

  const query = {
    filters,
    order,
    options,
  };

  try {
    dispatch(setFormStatus({ [FORM.query_builder]: FORM_STATUS.processing }));
    dispatch(setAlert({
      type: ALERT_TYPE.download,
      code: 'messages.download_started',
    }));
    const response = await postOpenTabEntityData(organisation, entity, query);
    if (response.status !== 200) {
      dispatch(setAlert({
        type: ALERT_TYPE.error,
        code: 'messages.download_error',
      }));
      dispatch(setFormStatus({ [FORM.query_builder]: FORM_STATUS.error }));
      return;
    }
    dispatch(setAlert({
      type: ALERT_TYPE.success,
      code: 'messages.download_success',
    }));
    dispatch(setFormStatus({ [FORM.query_builder]: FORM_STATUS.success }));
  } catch (error) {
    dispatch(setAlert({
      type: ALERT_TYPE.error,
      code: 'messages.download_error',
    }));
    dispatch(setFormStatus({ [FORM.query_builder]: FORM_STATUS.error }));
  }
};

export const postOpenTabEntityAction: (
  entity: ENTITIES,
  options: DownloadOptionsModel,
) => ThunkedAction<State> = (
  entity,
  options,
) => async (dispatch, getState) => {
  const organisation = getState().currentOrganisation.id;
  const contactId = getState().user.organisation_contact_id;

  const filters = {
    [SEARCH_LOGICAL_OPERATORS.and]: [
      [`${kebabToSnake(entity)}.contact_id`, SEARCH_OPERATORS_PRIMITIVE.eq, contactId],
    ],
  } as Filters;

  const query = {
    filters,
    options,
  };

  try {
    dispatch(setFormStatus({ [FORM.query_builder]: FORM_STATUS.processing }));
    dispatch(setAlert({
      type: ALERT_TYPE.download,
      code: 'messages.download_started',
    }));
    const response = await postOpenTabEntityData(organisation, entity, query);
    if (response.status !== 200) {
      dispatch(setAlert({
        type: ALERT_TYPE.error,
        code: 'messages.download_error',
      }));
      dispatch(setFormStatus({ [FORM.query_builder]: FORM_STATUS.error }));
      return;
    }
    dispatch(setAlert({
      type: ALERT_TYPE.success,
      code: 'messages.download_success',
    }));
    dispatch(setFormStatus({ [FORM.query_builder]: FORM_STATUS.success }));
  } catch (error) {
    dispatch(setAlert({
      type: ALERT_TYPE.error,
      code: 'messages.download_error',
    }));
    dispatch(setFormStatus({ [FORM.query_builder]: FORM_STATUS.error }));
  }
};

const normalizeEntities = (data: any[], entity: ENTITIES, timezone: string) => data.map(
  (value:any) => {
    switch (entity) {
      case ENTITIES.invoices:
        return normalizeInvoice(value, timezone);
      case ENTITIES.payments:
        return normalizePayment(value, timezone);
      default:
        return value;
    }
  },
);

export const getEntitiesAction:
(entity: ENTITIES) => ThunkedAction<State> =
  (entity: ENTITIES) => async (dispatch: any, getState: any) => {
    dispatch(setFormStatus({ [FORM.query_builder]: FORM_STATUS.loading }));
    const { querybuilder } = getState();
    const organisation = getState().currentOrganisation.id;
    const timezone = getState().data.timezones[getState().organisation.timezoneId];
    const payload = { ...querybuilder[entity] };
    const includeTemplate = entity === ENTITIES.invoices
      && getState().route.name === Route.INVOICES_SUBSCRIPTION;
    const response = await getEntitiesData(organisation, entity, payload, includeTemplate);
    if (response.success) {
      const { ids, count, result } = response.data;
      dispatch(setQueryBuilderResult({
        entity,
        result: {
          count,
          ids,
          list: [...normalizeEntities(result, entity, timezone)],
        } as QuerybuilderResult,
      }));
      dispatch(setFormStatus({ [FORM.query_builder]: FORM_STATUS.success }));
    } else {
      dispatch(setFormStatus({ [FORM.query_builder]: FORM_STATUS.error }));
    }
  };
